import { useEffect, useState } from 'react';
import { useAuth } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select, type SelectOption } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { goalSectorsApi, goalSubSectorsApi } from '../budgetGoalsApi';
import { sampleGoalSubSector } from '../utils/sampleBudgetCatalogs';

/**
 * Create or edit one goal sub-sector.
 *
 * The SECTOR can be changed on an edit — re-filing a sub-sector under the right sector is a
 * correction, not a reorder — and the server re-appends its position under the new parent.
 */
export function GoalSubSectorModal({
  open,
  editing,
  /**
   * Seeds and LOCKS the sector, for a create opened from a sector's own row.
   *
   * The reader chose the parent by choosing whose menu to open, so the picker has nothing left
   * to ask. The NAME travels with the id because `<Select>` resolves its label from its
   * options and a remote feed arrives a moment later — seeded with an id alone the field shows
   * its placeholder and reads as though no parent were set.
   */
  lockedSector,
  onClose,
  onSaved,
}: {
  open: boolean;
  /**
   * The row being corrected, or null to create.
   *
   * **Typed as the fields this form READS**, so the settings page's `GoalSubSectorListItem`
   * and the Goal Setting tree's node-plus-its-parent both satisfy it. One modal, two surfaces.
   */
  editing: {
    id: string;
    name: string;
    description: string | null;
    goalSectorId: string;
    sectorName: string;
  } | null;
  lockedSector?: { id: string; name: string };
  onClose: () => void;
  onSaved: () => void;
}) {
  const { hasPermission } = useAuth();

  const [sectorId, setSectorId] = useState(editing?.goalSectorId ?? lockedSector?.id ?? '');
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');

  /*
    Seeded with whatever the form already knows, so the field states its value from the FIRST
    paint rather than showing a placeholder until the feed lands.
  */
  const [sectors, setSectors] = useState<SelectOption[]>(() => {
    if (lockedSector) return [{ value: lockedSector.id, label: lockedSector.name }];
    if (editing) return [{ value: editing.goalSectorId, label: editing.sectorName }];
    return [];
  });

  /*
    A sector the reader adds from the picker is STAGED, not written.

    Writing it on the press left the catalog holding a row nobody chose every time a form was
    abandoned — four junk item types arrived in under a minute from one form that never saved,
    which is what made this the rule. The row is created inside `save()`, immediately before
    the sub-sector, so a vocabulary only grows when the transaction that needed the word
    actually completes.

    The residual gap is stated rather than hidden: the two stay SEPARATE calls under their own
    keys — folding the sector create into the sub-sector's own request would be the "a key one
    request shape gets around" hole — so a sub-sector the server rejects leaves the sector
    behind. That window is one request wide instead of however long the form was open.
  */
  const PENDING_SECTOR = 'pending:goal-sector';
  const [pendingSector, setPendingSector] = useState<string | null>(null);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    // A locked sector has nothing to pick from, so the feed is never fetched.
    if (lockedSector) return;

    const controller = new AbortController();

    goalSectorsApi
      .options(controller.signal)
      .then((rows) => {
        if (controller.signal.aborted) return;

        const options = rows.map((r) => ({ value: r.id, label: r.name }));

        /*
          A sub-sector filed under a DELETED sector keeps it — a deleted catalog row is left
          alone where it is already recorded — so the edit form injects it as an option. Without
          this the field would fall back to its placeholder and state that no sector is set,
          which is not what the record says.
        */
        if (editing && !options.some((o) => o.value === editing.goalSectorId)) {
          options.push({ value: editing.goalSectorId, label: editing.sectorName });
        }

        setSectors(options);
      })
      .catch(() => {
        // Fails soft: the field keeps whatever it was seeded with rather than emptying.
      });

    return () => controller.abort();
  }, [editing, lockedSector]);

  const valid = sectorId.length > 0 && name.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      /*
        The staged sector is written HERE, immediately before the row that needed it — so an
        abandoned form leaves the catalog untouched. Its own endpoint under its own key, and
        idempotent on the name, so two readers adding the same word get one row.
      */
      let resolvedSectorId = sectorId;

      if (pendingSector) {
        const created = await goalSectorsApi.create({ name: pendingSector, description: null });
        resolvedSectorId = created.id;
      }

      const request = {
        goalSectorId: resolvedSectorId,
        name: name.trim(),
        description: description.trim() || null,
      };

      if (editing) await goalSubSectorsApi.update(editing.id, request);
      else await goalSubSectorsApi.create(request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save the sub-sector.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'goal-sub-sector' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit sub-sector' : 'New sub-sector'}
      size="lg"
      id="goal-sub-sector-modal"
      autogenerate={{
        onGenerate: () => {
          const sample = sampleGoalSubSector();
          setName(sample.name);
          setDescription(sample.description);

          /*
            The SECTOR is filled too, and it has to be: the contract is that the primary action
            ENABLES on the first try, and on an unlocked create this is a required field the
            reader has not answered. Resolved against the live catalog — a real row from the
            options the form already loaded, never an invented id, which would fail the
            server's "it must exist and be active" check and read as a bug in the form.

            LOCKED is the case the never-generate-over-a-decided-field rule is about, and it is
            excluded: the reader chose the parent by choosing whose row they opened this on.
            A sector already chosen freely is left alone for the same reason.
          */
          if (!lockedSector && !sectorId && sectors.length > 0) {
            setSectorId(sectors[Math.floor(Math.random() * sectors.length)].value);
          }
        },
        disabled: saving,
      }}
      confirm={{
        id: 'goal-sub-sector-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="goal-sub-sector-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="goal-sub-sector-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <Select
          id="goal-sub-sector-goal-sector-id"
          label="Sector"
          required
          description={
            lockedSector
              ? 'Set from the sector this was opened on. Create from the Sub-sectors page to choose freely.'
              : 'The rung above this one. A sub-sector name is held once within its own sector.'
          }
          value={sectorId || null}
          onChange={(next) => {
            setSectorId(next);

            // Picking a real row drops the staged name, or the save would create a sector the
            // reader has since changed their mind about.
            if (next !== PENDING_SECTOR) setPendingSector(null);
          }}
          options={
            pendingSector
              ? // The staged name VERBATIM, so the field states what the reader typed rather
                // than falling back to its placeholder while the row does not exist yet.
                [{ value: PENDING_SECTOR, label: pendingSector }, ...sectors]
              : sectors
          }
          // The catalog reads in its own order, not alphabetically.
          sort="none"
          clearable={false}
          // Disabled for STATE, with the reason in its description — never hidden, because the
          // reader is allowed to see which sector this is filed under.
          disabled={saving || Boolean(lockedSector)}
          error={error?.fieldError('goalSectorId')}
          /*
            Adds a sector in place, gated on the OTHER module's key rather than this form's.
            The create is idempotent on the name, so two readers adding the same value get one
            row. Hidden without the key, along with the sentence that promises it.
          */
          allowCustom={!lockedSector && hasPermission('budget-goals.create')}
          onCreateOption={(raw) => {
            /*
              STAGED, not written — see `pendingSector`. Nothing reaches the catalog until the
              sub-sector itself is saved, so a form the reader abandons leaves no vocabulary
              behind.
            */
            setPendingSector(raw.trim());
            setSectorId(PENDING_SECTOR);
          }}
        />

        <TextField
          id="goal-sub-sector-name"
          label="Name"
          required
          description="What the sub-sector is called, e.g. Health. Held once within its sector — the same name may sit under two sectors."
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
          disabled={saving}
          error={error?.fieldError('name')}
        />

        <TextField
          id="goal-sub-sector-description"
          label="Description"
          description="Optional note for an administrator reading the catalog."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
